import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import OfferModal from "./OfferModal.jsx";
import ListingItem from "../components/ListingItem.jsx";
import { useAuth } from "./authContext";
import axios from 'axios';
import offMarketProperties from '../assets/investorlift_all_properties.json';
import MarketToggle from './MarketToggle.jsx';
import OffMarketModal from './OffMarketModal'; // Our renamed modal component

// Fix leaflet default icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});




const PriceDropdown = ({ priceType, setPriceType, priceMin, setPriceMin, priceMax, setPriceMax }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => setIsOpen(!isOpen);

  const minOptions = [
    { value: "Any", label: "Min" },
    { value: "50000", label: "$50k" },
    { value: "100000", label: "$100k" },
    { value: "150000", label: "$150k" },
    { value: "200000", label: "$200k" },
    { value: "250000", label: "$250k" }
  ];
  const maxOptions = [
    { value: "Any", label: "Max" },
    { value: "300000", label: "$300k" },
    { value: "400000", label: "$400k" },
    { value: "500000", label: "$500k" },
    { value: "600000", label: "$600k" },
    { value: "700000", label: "$700k" }
  ];

  return (
    <div style={priceDropdownStyles.container}>
      <div style={priceDropdownStyles.selected} onClick={toggleOpen}>
        <span>Price</span>
        <span style={priceDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <div style={priceDropdownStyles.dropdownPanel}>
          <div style={priceDropdownStyles.toggleContainer}>
            <button 
              style={{
                ...priceDropdownStyles.toggleButton,
                backgroundColor: priceType === "List Price" ? "#007aff" : "#fff",
                color: priceType === "List Price" ? "#fff" : "#000"
              }}
              onClick={() => setPriceType("List Price")}
            >
              List Price
            </button>
            <button 
              style={{
                ...priceDropdownStyles.toggleButton,
                backgroundColor: priceType === "Monthly Payment" ? "#007aff" : "#fff",
                color: priceType === "Monthly Payment" ? "#fff" : "#000"
              }}
              onClick={() => setPriceType("Monthly Payment")}
            >
              Monthly Payment
            </button>
          </div>
          <div style={priceDropdownStyles.rangeContainer}>
            <select 
              value={priceMin} 
              onChange={(e) => setPriceMin(e.target.value)}
              style={priceDropdownStyles.select}
            >
              {minOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select 
              value={priceMax} 
              onChange={(e) => setPriceMax(e.target.value)}
              style={priceDropdownStyles.select}
            >
              {maxOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button 
            style={priceDropdownStyles.applyButton}
            onClick={() => setIsOpen(false)}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

const priceDropdownStyles = {
  container: {
    position: 'relative',
    flex: 1,
  },
  selected: {
    padding: '10px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  arrow: {
    marginLeft: '8px',
  },
  dropdownPanel: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    marginTop: '4px',
    zIndex: 1000,
    padding: '10px',
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '10px',
  },
  toggleButton: {
    flex: 1,
    padding: '8px',
    border: '1px solid #007aff',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '5px',
  },
  rangeContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  select: {
    flex: 1,
    padding: '10px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    backgroundColor: '#fff',
  },
  applyButton: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

// ----- RadioDropdown Component for Listing Type -----
const RadioDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSelectedHovered, setIsSelectedHovered] = useState(false);
  const toggleOpen = () => setIsOpen(!isOpen);
  const handleOptionChange = (optionValue) => onChange(optionValue);
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div style={customDropdownStyles.container}>
      <div
        style={{
          ...customDropdownStyles.selected,
          backgroundColor: isSelectedHovered ? '#f8f8f8' : '#fff',
        }}
        onMouseEnter={() => setIsSelectedHovered(true)}
        onMouseLeave={() => setIsSelectedHovered(false)}
        onClick={toggleOpen}
      >
        <span>{selectedOption ? selectedOption.label : ''}</span>
        <span style={customDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <ul
          style={{
            ...customDropdownStyles.options,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
          }}
        >
          {options.map((option) => {
            // Disable "For Rent" and "Sold"
            const isDisabled = option.value === "For Rent" || option.value === "Sold";
            return (
              <li key={option.value} style={customDropdownStyles.option}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    color: isDisabled ? "#aaa" : "inherit",
                  }}
                >
                  <input
                    type="radio"
                    value={option.value}
                    checked={value === option.value}
                    onChange={() => !isDisabled && handleOptionChange(option.value)}
                    disabled={isDisabled}
                    style={{ marginRight: "5px" }}
                  />
                  {option.label}
                </label>
              </li>
            );
          })}
          <li style={{ padding: '10px', textAlign: 'center' }}>
            <button
              style={{
                display: 'block',
                width: '100%',
                backgroundColor: '#007aff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
              onClick={() => setIsOpen(false)}
            >
              Apply
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

// ----- CheckboxDropdown Component for Property Type -----
const CheckboxDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(value);
  const [isSelectedHovered, setIsSelectedHovered] = useState(false);

  useEffect(() => {
    setTempSelection(value);
  }, [value]);

  const toggleOpen = () => setIsOpen(!isOpen);
  const handleCheckboxChange = (optionValue) => {
    if (tempSelection.includes(optionValue)) {
      setTempSelection(tempSelection.filter(val => val !== optionValue));
    } else {
      setTempSelection([...tempSelection, optionValue]);
    }
  };
  const handleApply = () => {
    onChange(tempSelection);
    setIsOpen(false);
  };

  const selectedText = "Property Type";

  return (
    <div style={{ ...customDropdownStyles.container, width: '200px' }}>
      <div
        style={{
          ...customDropdownStyles.selected,
          backgroundColor: isSelectedHovered ? '#f8f8f8' : '#fff',
        }}
        onMouseEnter={() => setIsSelectedHovered(true)}
        onMouseLeave={() => setIsSelectedHovered(false)}
        onClick={toggleOpen}
      >
        <span>{selectedText}</span>
        <span style={customDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <ul
          style={{
            ...customDropdownStyles.options,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxHeight: 'none',
            overflowY: 'visible'
          }}
        >
          {options.map((option) => (
            <li key={option.value} style={customDropdownStyles.option}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  value={option.value}
                  checked={tempSelection.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  style={{ marginRight: "5px" }}
                />
                {option.label}
              </label>
            </li>
          ))}
          <li style={{ padding: '10px', textAlign: 'center' }}>
            <button
              style={{
                display: 'block',
                width: '100%',
                backgroundColor: '#007aff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
              onClick={handleApply}
            >
              Apply
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

const BedroomsBathroomsDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempBedrooms, setTempBedrooms] = useState(value.bedrooms || "Any");
  const [tempBathrooms, setTempBathrooms] = useState(value.bathrooms || "Any");
  const [isSelectedHovered, setIsSelectedHovered] = useState(false);

  useEffect(() => {
    setTempBedrooms(value.bedrooms || "Any");
    setTempBathrooms(value.bathrooms || "Any");
  }, [value]);

  const toggleOpen = () => setIsOpen(!isOpen);
  const handleApply = () => {
    onChange({ bedrooms: tempBedrooms, bathrooms: tempBathrooms });
    setIsOpen(false);
  };

  const optionStyle = (selected) => ({
    padding: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    backgroundColor: selected ? '#e0f0ff' : '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    flex: '1',
    textAlign: 'center',
    margin: '2px'
  });

  const bedroomOptions = ["Any", "1+", "2+", "3+", "4+", "5+"];
  const bathroomOptions = ["Any", "1+", "2+", "3+", "4+", "5+"];

  const selectedText = "Beds & Baths";

  return (
    <div style={{ ...customDropdownStyles.container, width: '400px' }}>
      <div
        style={{
          ...customDropdownStyles.selected,
          backgroundColor: isSelectedHovered ? '#f8f8f8' : '#fff',
        }}
        onMouseEnter={() => setIsSelectedHovered(true)}
        onMouseLeave={() => setIsSelectedHovered(false)}
        onClick={toggleOpen}
      >
        <span>{selectedText}</span>
        <span style={customDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <div
          style={{
            ...customDropdownStyles.options,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '200%',
            maxHeight: 'none',
            overflowY: 'visible',
            padding: '10px'
          }}
        >
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Bedrooms</div>
            <div style={{ display: 'flex' }}>
              {bedroomOptions.map((option) => (
                <div
                  key={option}
                  style={optionStyle(tempBedrooms === option)}
                  onClick={() => setTempBedrooms(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Bathrooms</div>
            <div style={{ display: 'flex' }}>
              {bathroomOptions.map((option) => (
                <div
                  key={option}
                  style={optionStyle(tempBathrooms === option)}
                  onClick={() => setTempBathrooms(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <button
              style={{
                display: 'block',
                width: '100%',
                backgroundColor: '#007aff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const customDropdownStyles = {
  container: {
    position: 'relative',
    flex: 1,
    width: 120,
  },
  selected: {
    padding: '10px',
    fontSize: '16px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  arrow: {
    marginLeft: '8px',
  },
  options: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    marginTop: '4px',
    zIndex: 1000,
    listStyle: 'none',
    padding: 0,
    maxHeight: '200px',
    overflowY: 'auto',
  },
  option: {
    padding: '10px',
    fontSize: '16px',
    cursor: 'pointer',
  },
};

/* =====================================
   OffMarketSearchBar Component
===================================== */
// This component now uses a controlled input to filter off-market properties automatically.
// It accepts offMarketProperties (defaulting to an empty array) and calls onSearch with the filtered results.

const OffMarketSearchBar = ({ offMarketProperties = [], onSearch }) => {
  const [offMarketSearchText, setOffMarketSearchText] = useState('');

  const filteredOffMarketProperties = useMemo(() => {
    if (!offMarketSearchText.trim()) return offMarketProperties;
    const query = offMarketSearchText.toLowerCase();
    return offMarketProperties.filter(listing => {
      return (
        (listing.city && listing.city.toLowerCase().includes(query)) ||
        (listing.state && listing.state.toLowerCase().includes(query))
      );
    });
  }, [offMarketSearchText, offMarketProperties]);

  useEffect(() => {
    if (onSearch) {
      onSearch(filteredOffMarketProperties);
    }
  }, [filteredOffMarketProperties, onSearch]);

  return (
    <div style={{ marginBottom: '30px' }}>
      <input
        type="text"
        placeholder="Search Off-Market by City or State"
        value={offMarketSearchText}
        onChange={(e) => setOffMarketSearchText(e.target.value)}
        style={{
          padding: '10px',
          fontSize: '16px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          width: '100%',
        }}
      />
    </div>
  );
};





/* =====================================
   ChangeMapView Component
===================================== */
const ChangeMapView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center);
  }, [center, map]);
  return null;
};

const ZillowSearchBar = ({ onSearch }) => {
  const [marketStatus, setMarketStatus] = useState('on');

  // Location search states
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Filter states – note we use priceMin/priceMax from PriceDropdown.
  const [listingType, setListingType] = useState("For Sale");
  const [propertyType, setPropertyType] = useState([]);
  const [bedroomsBathrooms, setBedroomsBathrooms] = useState({
    bedrooms: "Any",
    bathrooms: "Any",
  });
  const [priceType, setPriceType] = useState("List Price");
  const [priceMin, setPriceMin] = useState("Any");
  const [priceMax, setPriceMax] = useState("Any");

  const listingTypeOptions = [
    { value: "For Sale", label: "For Sale" },
    { value: "For Rent", label: "For Rent - Coming Soon" },
    { value: "Sold", label: "Sold - Coming Soon" },
  ];
  const propertyTypeOptions = [
    { value: "Houses", label: "Houses" },
    { value: "Townhomes", label: "Townhomes" },
    { value: "Multi-Family", label: "Multi-Family" },
    { value: "Condos/Co-Ops", label: "Condos/Co-Ops" },
    { value: "Lots/Land", label: "Lots/Land" },
    { value: "Apartments", label: "Apartments" },
    { value: "Manufactured", label: "Manufactured" },
  ];

  // States and cities mapping
  const states = [
    { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' }, { label: 'Arizona', value: 'AZ' },
    { label: 'Arkansas', value: 'AR' }, { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
    { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
    { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' }, { label: 'Idaho', value: 'ID' },
    { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
    { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' }, { label: 'Louisiana', value: 'LA' },
    { label: 'Maine', value: 'ME' }, { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
    { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' }, { label: 'Mississippi', value: 'MS' },
    { label: 'Missouri', value: 'MO' }, { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
    { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
    { label: 'New Mexico', value: 'NM' }, { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
    { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' }, { label: 'Oklahoma', value: 'OK' },
    { label: 'Oregon', value: 'OR' }, { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
    { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' }, { label: 'Tennessee', value: 'TN' },
    { label: 'Texas', value: 'TX' }, { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
    { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' }, { label: 'West Virginia', value: 'WV' },
    { label: 'Wisconsin', value: 'WI' }, { label: 'Wyoming', value: 'WY' }
  ];
  const citiesByState = {
    AL: ['Birmingham', 'Montgomery', 'Huntsville', 'Mobile', 'Tuscaloosa', 'Hoover', 'Dothan', 'Auburn', 'Decatur', 'Madison',
         'Florence', 'Gadsden', 'Vestavia Hills', 'Prattville', 'Phenix City', 'Alabaster', 'Bessemer', 'Opelika', 'Enterprise', 'Homewood'],
    AK: ['Anchorage', 'Fairbanks', 'Juneau', 'Sitka', 'Ketchikan', 'Wasilla', 'Kenai', 'Kodiak', 'Bethel', 'Palmer',
         'Homer', 'Seward', 'Valdez', 'Nome', 'Kotzebue', 'Wrangell', 'Petersburg', 'Dillingham', 'Cordova', 'Barrow'],
    AZ: ['Phoenix', 'Tucson', 'Mesa', 'Chandler', 'Scottsdale', 'Glendale', 'Gilbert', 'Tempe', 'Peoria', 'Surprise',
         'Yuma', 'Flagstaff', 'Goodyear', 'Lake Havasu City', 'Buckeye', 'Casa Grande', 'Sierra Vista', 'Maricopa', 'Prescott', 'Bullhead City'],
    CA: ['Los Angeles', 'San Diego', 'San Jose', 'San Francisco', 'Fresno', 'Sacramento', 'Long Beach', 'Oakland', 'Bakersfield', 'Anaheim',
         'Santa Ana', 'Riverside', 'Stockton', 'Irvine', 'Chula Vista', 'Fremont', 'San Bernardino', 'Modesto', 'Fontana', 'Oxnard'],
    TX: ['Houston', 'San Antonio', 'Dallas', 'Austin', 'Fort Worth', 'El Paso', 'Arlington', 'Corpus Christi', 'Plano', 'Lubbock',
         'Laredo', 'Garland', 'Irving', 'Amarillo', 'Grand Prairie', 'McKinney', 'Frisco', 'Brownsville', 'Pasadena', 'Killeen'],
    FL: ['Miami', 'Tampa', 'Orlando', 'Jacksonville', 'Tallahassee', 'Fort Lauderdale', 'St. Petersburg', 'Hialeah', 'Gainesville', 'Sarasota',
         'Naples', 'West Palm Beach', 'Pensacola', 'Lakeland', 'Fort Myers', 'Daytona Beach', 'Boca Raton', 'Delray Beach', 'Coral Gables', 'Hollywood'],
    NY: ['New York City', 'Buffalo', 'Rochester', 'Yonkers', 'Syracuse', 'Albany', 'New Rochelle', 'Mount Vernon', 'Schenectady', 'Utica',
         'White Plains', 'Hempstead', 'Troy', 'Niagara Falls', 'Binghamton', 'Freeport', 'Valley Stream', 'Rome', 'Ithaca', 'Poughkeepsie'],
    GA: ['Atlanta', 'Augusta', 'Columbus', 'Savannah', 'Athens', 'Sandy Springs', 'Roswell', 'Macon', 'Johns Creek', 'Albany',
         'Warner Robins', 'Alpharetta', 'Marietta', 'Valdosta', 'Smyrna', 'Dunwoody', 'Rome', 'Peachtree City', 'Gainesville', 'Brookhaven'],
    NC: ['Charlotte', 'Raleigh', 'Greensboro', 'Durham', 'Winston-Salem', 'Fayetteville', 'Cary', 'Wilmington', 'High Point', 'Greenville',
         'Asheville', 'Concord', 'Gastonia', 'Jacksonville', 'Rocky Mount', 'Burlington', 'Wilson', 'Kannapolis', 'Goldsboro', 'Chapel Hill'],
    OH: ['Columbus', 'Cleveland', 'Cincinnati', 'Toledo', 'Akron', 'Dayton', 'Parma', 'Canton', 'Youngstown', 'Lorain',
         'Hamilton', 'Springfield', 'Kettering', 'Elyria', 'Lakewood', 'Cuyahoga Falls', 'Middletown', 'Newark', 'Dublin', 'Beavercreek'],
    PA: ['Philadelphia', 'Pittsburgh', 'Allentown', 'Erie', 'Reading', 'Scranton', 'Bethlehem', 'Lancaster', 'Harrisburg', 'York',
         'Altoona', 'Wilkes-Barre', 'State College', 'Chester', 'Bethel Park', 'Monroeville', 'Williamsport', 'Norristown', 'Mount Lebanon', 'Easton'],
    IL: ['Chicago', 'Aurora', 'Naperville', 'Joliet', 'Rockford', 'Springfield', 'Elgin', 'Peoria', 'Champaign', 'Waukegan',
         'Cicero', 'Bloomington', 'Arlington Heights', 'Evanston', 'Decatur', 'Schaumburg', 'Bolingbrook', 'Palatine', 'Skokie', 'Des Plaines'],
    WA: ['Seattle', 'Spokane', 'Tacoma', 'Vancouver', 'Bellevue', 'Kent', 'Everett', 'Renton', 'Yakima', 'Federal Way',
         'Spokane Valley', 'Bellingham', 'Kennewick', 'Auburn', 'Pasco', 'Marysville', 'Lakewood', 'Redmond', 'Shoreline', 'Kirkland'],
    VA: ['Virginia Beach', 'Norfolk', 'Chesapeake', 'Richmond', 'Newport News', 'Alexandria', 'Hampton', 'Roanoke', 'Portsmouth', 'Suffolk',
         'Lynchburg', 'Harrisonburg', 'Charlottesville', 'Danville', 'Manassas', 'Petersburg', 'Fredericksburg', 'Winchester', 'Staunton', 'Salem'],
    TN: ['Memphis', 'Nashville', 'Knoxville', 'Chattanooga', 'Clarksville', 'Murfreesboro', 'Franklin', 'Jackson', 'Johnson City', 'Bartlett',
         'Hendersonville', 'Kingsport', 'Collierville', 'Cleveland', 'Smyrna', 'Germantown', 'Brentwood', 'Columbia', 'Spring Hill', 'Gallatin'],
    CO: ['Denver', 'Colorado Springs', 'Aurora', 'Fort Collins', 'Lakewood', 'Thornton', 'Arvada', 'Westminster', 'Pueblo', 'Centennial',
         'Boulder', 'Greeley', 'Longmont', 'Loveland', 'Broomfield', 'Grand Junction', 'Castle Rock', 'Commerce City', 'Parker', 'Littleton'],
    OR: ['Portland', 'Eugene', 'Salem', 'Gresham', 'Hillsboro', 'Beaverton', 'Bend', 'Medford', 'Springfield', 'Corvallis',
         'Albany', 'Tigard', 'Lake Oswego', 'Keizer', 'Grants Pass', 'McMinnville', 'Redmond', 'Tualatin', 'West Linn', 'Woodburn'],
    NV: ['Las Vegas', 'Henderson', 'Reno', 'North Las Vegas', 'Paradise', 'Spring Valley', 'Sunrise Manor', 'Enterprise', 'Sparks', 'Carson City',
         'Whitney', 'Pahrump', 'Summerlin South', 'Fernley', 'Elko', 'Mesquite', 'Boulder City', 'Spanish Springs', 'Winchester', 'Laughlin'],
         KS: ['Wichita', 'Overland Park', 'Kansas City', 'Olathe', 'Topeka', 'Lawrence', 'Shawnee', 'Manhattan', 'Lenexa', 'Salina',
         'Hutchinson', 'Leavenworth', 'Leawood', 'Dodge City', 'Garden City', 'Emporia', 'Derby', 'Prairie Village', 'Junction City', 'Pittsburg'],
    KY: ['Louisville', 'Lexington', 'Bowling Green', 'Owensboro', 'Covington', 'Richmond', 'Georgetown', 'Florence', 'Elizabethtown', 'Hopkinsville',
         'Nicholasville', 'Henderson', 'Frankfort', 'Jeffersontown', 'Independence', 'Paducah', 'Radcliff', 'Ashland', 'Madisonville', 'Erlanger'],
    LA: ['New Orleans', 'Baton Rouge', 'Shreveport', 'Lafayette', 'Lake Charles', 'Kenner', 'Bossier City', 'Monroe', 'Alexandria', 'Houma',
         'Slidell', 'Marrero', 'Central', 'New Iberia', 'Laplace', 'Ruston', 'Sulphur', 'Hammond', 'Natchitoches', 'Gretna'],
    ME: ['Portland', 'Lewiston', 'Bangor', 'South Portland', 'Auburn', 'Biddeford', 'Sanford', 'Saco', 'Westbrook', 'Augusta',
         'Waterville', 'Presque Isle', 'Brewer', 'Bath', 'Ellsworth', 'Caribou', 'Old Town', 'Rockland', 'Skowhegan', 'Orono'],
    MD: ['Baltimore', 'Columbia', 'Germantown', 'Silver Spring', 'Waldorf', 'Ellicott City', 'Frederick', 'Dundalk', 'Rockville', 'Bethesda',
         'Gaithersburg', 'Bowie', 'Towson', 'Hagerstown', 'Bel Air South', 'Annapolis', 'Severna Park', 'Catonsville', 'Odenton', 'Randallstown'],
    MA: ['Boston', 'Worcester', 'Springfield', 'Cambridge', 'Lowell', 'Brockton', 'New Bedford', 'Quincy', 'Lynn', 'Fall River',
         'Newton', 'Lawrence', 'Somerville', 'Waltham', 'Haverhill', 'Malden', 'Medford', 'Taunton', 'Chicopee', 'Everett'],
    MI: ['Detroit', 'Grand Rapids', 'Warren', 'Sterling Heights', 'Ann Arbor', 'Lansing', 'Flint', 'Dearborn', 'Livonia', 'Westland',
         'Troy', 'Farmington Hills', 'Kalamazoo', 'Wyoming', 'Southfield', 'Rochester Hills', 'Taylor', 'Pontiac', 'St. Clair Shores', 'Royal Oak'],
    MN: ['Minneapolis', 'St. Paul', 'Rochester', 'Duluth', 'Bloomington', 'Brooklyn Park', 'Plymouth', 'Maple Grove', 'Woodbury', 'St. Cloud',
         'Eagan', 'Eden Prairie', 'Coon Rapids', 'Burnsville', 'Blaine', 'Lakeville', 'Minnetonka', 'Apple Valley', 'Edina', 'Mankato'],
         MS: ['Jackson', 'Gulfport', 'Southaven', 'Biloxi', 'Hattiesburg', 'Olive Branch', 'Tupelo', 'Meridian', 'Greenville', 'Horn Lake',
         'Madison', 'Starkville', 'Clinton', 'Ridgeland', 'Pascagoula', 'Vicksburg', 'Brandon', 'Columbus', 'Pearl', 'Oxford'],
    MO: ['Kansas City', 'St. Louis', 'Springfield', 'Columbia', 'Independence', `Lee's Summit`, `O'Fallon`, 'St. Joseph', 'St. Charles', 'Blue Springs',
         'Florissant', 'Joplin', 'Chesterfield', 'Jefferson City', 'Cape Girardeau', 'Wildwood', 'University City', 'Ballwin', 'Raytown', 'Liberty'],
         MT: ['Billings', 'Missoula', 'Great Falls', 'Bozeman', 'Butte', 'Helena', 'Kalispell', 'Havre', 'Anaconda', 'Miles City',
         'Belgrade', 'Livingston', 'Laurel', 'Whitefish', 'Sidney', 'Lewistown', 'Glendive', 'Columbia Falls', 'Polson', 'Hamilton'],
         CT: ['Bridgeport', 'New Haven', 'Stamford', 'Hartford', 'Waterbury', 'Norwalk', 'Danbury', 'New Britain', 'Meriden', 'Bristol',
         'West Haven', 'Middletown', 'Milford', 'Shelton', 'Norwich', 'Torrington', 'New London', 'Ansonia', 'Derby', 'Groton'],
    DE: ['Wilmington', 'Dover', 'Newark', 'Middletown', 'Smyrna', 'Milford', 'Seaford', 'Georgetown', 'Elsmere', 'New Castle',
         'Millsboro', 'Laurel', 'Harrington', 'Camden', 'Clayton', 'Lewes', 'Selbyville', 'Milton', 'Bridgeville', 'Delmar'],
    NE: ['Omaha', 'Lincoln', 'Bellevue', 'Grand Island', 'Kearney', 'Fremont', 'Hastings', 'Norfolk', 'North Platte', 'Columbus',
         'Papillion', 'La Vista', 'Scottsbluff', 'South Sioux City', 'Beatrice', 'Lexington', 'Alliance', 'Gering', 'Blair', 'York'],
    NH: ['Manchester', 'Nashua', 'Concord', 'Dover', 'Rochester', 'Keene', 'Portsmouth', 'Laconia', 'Lebanon', 'Claremont',
         'Somersworth', 'Berlin', 'Exeter', 'Hampton', 'Hanover', 'Merrimack', 'Milford', 'Raymond', 'Hooksett', 'Goffstown'],
    NJ: ['Newark', 'Jersey City', 'Paterson', 'Elizabeth', 'Edison', 'Woodbridge', 'Lakewood', 'Toms River', 'Hamilton', 'Trenton',
         'Clifton', 'Camden', 'Brick', 'Cherry Hill', 'Passaic', 'Union City', 'Old Bridge', 'Franklin', 'Middletown', 'Bayonne'],
    NM: ['Albuquerque', 'Las Cruces', 'Rio Rancho', 'Santa Fe', 'Roswell', 'Farmington', 'Clovis', 'Hobbs', 'Alamogordo', 'Carlsbad',
         'Gallup', 'Los Lunas', 'Deming', 'Las Vegas', 'Sunland Park', 'Portales', 'Silver City', 'Artesia', 'Espanola', 'Ruidoso'],
    ND: ['Fargo', 'Bismarck', 'Grand Forks', 'Minot', 'West Fargo', 'Williston', 'Dickinson', 'Mandan', 'Jamestown', 'Wahpeton',
         'Devils Lake', 'Valley City', 'Grafton', 'Beulah', 'Horace', 'Watford City', 'Lincoln', 'Casselton', 'Bottineau', 'Rugby'],
    OK: ['Oklahoma City', 'Tulsa', 'Norman', 'Broken Arrow', 'Edmond', 'Lawton', 'Moore', 'Midwest City', 'Stillwater', 'Enid',
         'Muskogee', 'Bartlesville', 'Owasso', 'Shawnee', 'Yukon', 'Bixby', 'Ardmore', 'Ponca City', 'Duncan', 'Del City'],
    RI: ['Providence', 'Warwick', 'Cranston', 'Pawtucket', 'East Providence', 'Woonsocket', 'Newport', 'Central Falls', 'Westerly', 'Valley Falls',
         'Smithfield', 'Coventry', 'North Providence', 'Tiverton', 'Portsmouth', 'Barrington', 'Middletown', 'Lincoln', 'Bristol', 'Narragansett'],
    SC: ['Columbia', 'Charleston', 'North Charleston', 'Mount Pleasant', 'Rock Hill', 'Greenville', 'Summerville', 'Goose Creek', 'Sumter', 'Florence',
         'Spartanburg', 'Myrtle Beach', 'Anderson', 'Aiken', 'Hilton Head Island', 'Greer', 'Mauldin', 'Greenwood', 'North Augusta', 'Easley'],
    SD: ['Sioux Falls', 'Rapid City', 'Aberdeen', 'Brookings', 'Watertown', 'Mitchell', 'Yankton', 'Pierre', 'Huron', 'Spearfish',
         'Vermillion', 'Brandon', 'Box Elder', 'Madison', 'Sturgis', 'Belle Fourche', 'Harrisburg', 'Tea', 'Dell Rapids', 'Canton'],
    UT: ['Salt Lake City', 'West Valley City', 'Provo', 'West Jordan', 'Orem', 'Sandy', 'Ogden', 'St. George', 'Layton', 'Taylorsville',
         'South Jordan', 'Lehi', 'Logan', 'Murray', 'Draper', 'Bountiful', 'Riverton', 'Roy', 'Spanish Fork', 'Pleasant Grove'],
    VT: ['Burlington', 'South Burlington', 'Rutland', 'Barre', 'Montpelier', 'Winooski', 'St. Albans', 'Newport', 'Essex Junction', 'Bellows Falls',
         'Brattleboro', 'Bennington', 'Middlebury', 'Stowe', 'Hartford', 'Colchester', 'Shelburne', 'Northfield', 'Waterbury', 'Lyndon'],
    WV: ['Charleston', 'Huntington', 'Morgantown', 'Parkersburg', 'Wheeling', 'Weirton', 'Fairmont', 'Martinsburg', 'Beckley', 'Clarksburg',
         'South Charleston', 'Bluefield', 'Moundsville', 'Bridgeport', 'Vienna', 'St. Albans', 'Elkins', 'Dunbar', 'Hurricane', 'Oak Hill'],
  };

  const handleApply = () => {
    if (onSearch) {
      // Build filters – convert empty strings to undefined.
      // For "List Price", we pass priceMin and priceMax as numbers.
      const filters = {
        listingType,
        propertyType: propertyType.length > 0 ? propertyType.join(",") : undefined,
        bedrooms: bedroomsBathrooms.bedrooms !== "Any" ? Number(bedroomsBathrooms.bedrooms.replace("+", "")) : undefined,
        bathrooms: bedroomsBathrooms.bathrooms !== "Any" ? Number(bedroomsBathrooms.bathrooms.replace("+", "")) : undefined,
        priceType,
      };

      if (priceType === "List Price") {
        filters.priceMin = priceMin !== "Any" ? Number(priceMin) : undefined;
        filters.priceMax = priceMax !== "Any" ? Number(priceMax) : undefined;
      }
      onSearch({
        state: selectedState,
        city: selectedCity,
        zipCode,
        ...filters,
      });
    }
  };

  return (
    <div style={searchBarStyles.container}>
      {/* Row 1: Location inputs */}
      <div style={searchBarStyles.locationContainer}>
        <select
          value={selectedState}
          onChange={(e) => {
            setSelectedState(e.target.value);
            setSelectedCity("");
          }}
          style={searchBarStyles.select}
        >
          <option value="">Select State</option>
          {states.map((state) => (
            <option key={state.value} value={state.value}>
              {state.label}
            </option>
          ))}
        </select>

        <select
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          style={searchBarStyles.select}
          disabled={!selectedState}
        >
          {selectedState ? (
            <>
              <option value="">Select City</option>
              {citiesByState[selectedState]?.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </>
          ) : (
            <option value="">Select State First</option>
          )}
        </select>

        <span style={searchBarStyles.orText}>OR</span>

        <input
          type="text"
          placeholder="Enter Zip Code"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          style={searchBarStyles.input}
        />
      </div>

      {/* Row 2: Additional Filters */}
      <div style={searchBarStyles.filtersContainer}>
        <PriceDropdown
          priceType={priceType}
          setPriceType={setPriceType}
          priceMin={priceMin}
          setPriceMin={setPriceMin}
          priceMax={priceMax}
          setPriceMax={setPriceMax}
        />
        <RadioDropdown
          value={listingType}
          onChange={setListingType}
          options={listingTypeOptions}
        />
        <CheckboxDropdown
          value={propertyType}
          onChange={setPropertyType}
          options={propertyTypeOptions}
        />
        <BedroomsBathroomsDropdown
          value={bedroomsBathrooms}
          onChange={setBedroomsBathrooms}
        />
      </div>

      <div style={searchBarStyles.buttonContainer}>
        <button onClick={handleApply} style={searchBarStyles.button}>
          Search
        </button>
      </div>
     
    </div>
  );
};
const toggleStyles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '10px',
  },
  button: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    margin: '0 5px',
    fontSize: '16px',
  },
  active: {
    backgroundColor: '#007aff',
    color: '#fff',
  },
  inactive: {
    backgroundColor: '#f2f2f2',
    color: '#333',
  },
};
const searchBarStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
  },
  locationContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  filtersContainer: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  select: {
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    flex: "1",
    minWidth: "150px",
  },
  input: {
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    flex: "2",
    minWidth: "150px",
  },
  orText: {
    fontSize: "16px",
    fontWeight: "bold",
    margin: "0 10px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
  },
  button: {
    padding: "12px 24px",
    fontSize: "16px",
    backgroundColor: "#007aff",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
};
/* =====================================
   Zillow Clone Page Component
===================================== */
const dummyListings = [
  {
    id: "1",
    state: "TN",
    city: "Memphis",
    zipCode: "38128",
    listingType: "For Sale",
    propertyType: "Single Family",
    price: 300000,
    bedrooms: 3,
    bathrooms: 2,
    formattedAddress: "123 Main St, Memphis, TN 38128",
    imgSrc: "https://via.placeholder.com/150?text=Default+Image+1",
    latitude: 35.1495,
    longitude: -90.0490,
  },
  {
    id: "2",
    state: "GA",
    city: "Augusta",
    zipCode: "30907",
    listingType: "For Rent",
    propertyType: "Townhomes",
    price: 1500,
    bedrooms: 2,
    bathrooms: 1,
    formattedAddress: "456 Oak Ave, Augusta, GA 30907",
    imgSrc: "https://via.placeholder.com/150?text=Default+Image+2",
    latitude: 33.4735,
    longitude: -82.0101,
  },
  {
    id: "3",
    state: "AL",
    city: "Birmingham",
    zipCode: "35205",
    listingType: "For Sale",
    propertyType: "Multi-Family",
    price: 500000,
    bedrooms: 4,
    bathrooms: 3,
    formattedAddress: "789 Pine Rd, Birmingham, AL 35205",
    imgSrc: "https://via.placeholder.com/150?text=Default+Image+3",
    latitude: 33.5207,
    longitude: -86.8025,
  }
];

const ZillowClonePage = ({ listings: defaultListings = dummyListings }) => {
  const [mapCenter, setMapCenter] = useState([35.1495, -90.0490]);
  const { currentUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showOffMarket, setShowOffMarket] = useState(false);
  // Save on-market listings so we can revert back.
  const [onMarketListings, setOnMarketListings] = useState(defaultListings);
  const [listings, setListings] = useState(defaultListings);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [listingDetails, setListingDetails] = useState(null);
  const [useStreetView, setUseStreetView] = useState(false);
  const [visibleCount, setVisibleCount] = useState(4);
  const loadMoreRef = useRef(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [listingOffMarketDetails, setListingOffMarketDetails] = useState(null);
  const [isOffMarketModalOpen, setIsOffMarketModalOpen] = useState(false);

  useEffect(() => {
    if (listings.length > 0) {
      const { latitude, longitude } = listings[0];
      const latNum = Number(latitude);
      const lngNum = Number(longitude);
      if (!isNaN(latNum) && !isNaN(lngNum)) {
        setMapCenter([latNum, lngNum]);
      } else {
        // Fallback center if coordinates are invalid
        setMapCenter([39.8283, -98.5795]);
      }
    } else {
      setMapCenter([39.8283, -98.5795]);
    }
  }, [listings]);
  


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // When the sentinel is visible, load 4 more listings (up to the total)
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 4, listings.length));
        }
      },
      { threshold: 1 }
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [listings.length]);
  // Update map center.
  useEffect(() => {
    if (listings.length > 0) {
      if (!showOffMarket && listings[0].latitude && listings[0].longitude) {
        setMapCenter([listings[0].latitude, listings[0].longitude]);
      } else {
        setMapCenter([39.8283, -98.5795]); // default center for off-market
      }
    }
  }, [listings, showOffMarket]);

  // API call: on-market listings.
  const fetchListings = async (params) => {
    try {
      const response = await axios.get(`https://api.rentcast.io/v1/listings/sale`, {
        params: {
          state: params.state,
          city: params.city,
          listingType: params.listingType,
          propertyType: params.propertyType,
          priceMin: params.priceMin,
          priceMax: params.priceMax,
          bedrooms: params.bedrooms,
          bathrooms: params.bathrooms,
          priceType: params.priceType,
          saleDateRange: 300,
          status: "Active",
          limit: 30,
        },
        headers: {
          "X-API-Key": "3bf99588906842468bdd81d2e38d0083",
        },
      });
      setListings(response.data);
      setOnMarketListings(response.data); // Update onMarketListings
      setIsSearchTriggered(true);
    } catch (error) {
      console.error("Error fetching listings by state and city:", error);
    }
  };
  

  // API call: on-market by ZIP.
  const fetchListingsByZip = async (params) => {
    try {
      const response = await axios.get(`https://api.rentcast.io/v1/listings/sale`, {
        params: {
          zipCode: params.zipCode,
          listingType: params.listingType,
          propertyType: params.propertyType,
          priceMin: params.priceMin,
          priceMax: params.priceMax,
          bedrooms: params.bedrooms,
          bathrooms: params.bathrooms,
          priceType: params.priceType,
          saleDateRange: 300,
          status: "Active",
          limit: 30,
        },
        headers: {
          "X-API-Key": "3bf99588906842468bdd81d2e38d0083",
        },
      });
      setListings(response.data);
      setIsSearchTriggered(true);
    } catch (error) {
      console.error("Error fetching listings by ZIP code:", error);
    }
  };

  // Handle on-market search.
  const handleSearch = (params) => {

    if (params.zipCode && params.zipCode.trim() !== "") {
      fetchListingsByZip(params);
    } else if (params.state && params.city) {
      fetchListings(params);
    } else {
      setListings(defaultListings);
      setIsSearchTriggered(false);
    }
    setUseStreetView(true);
  };

  // Handle off-market search: The OffMarketSearchBar now filters offMarketProperties automatically.
  // Its onSearch callback receives the filtered properties, which we use to update the listings.
  const handleOffMarketResults = (filteredResults) => {
    setListings(filteredResults);
  };

  // In your React component
  const handleListingClick = async (listing) => {
    setSelectedListing(listing);
    setListingOffMarketDetails(null);
  
    const isOffMarket = !listing.formattedAddress;
  
    if (isOffMarket) {
      setIsOffMarketModalOpen(true); // Show loading modal immediately
  
      try {
        const response = await fetch("https://api-vasejd2hkq-uc.a.run.app/getListingDetails", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listingId: listing.id }),
        });
  
        const details = await response.json();
        console.log(details)
        setListingOffMarketDetails(details);
      } catch (error) {
        console.error("Error fetching off-market details:", error);
      }
    } else {
      // Show your regular on-market modal
      setIsModalOpen(true);
    }
  };
  
  
  
  
  
  
  
  
  

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedListing(null);
  };


  const closeOffMarketModal = () => {
    setIsOffMarketModalOpen(false);
    setListingOffMarketDetails(null);
  };
  const handleToggleOffMarket = () => {
    const newValue = !showOffMarket;
    setShowOffMarket(newValue);
    if (newValue) {
      setListings(offMarketProperties);
    } else {
      setListings(onMarketListings);
    }
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padidng: '20px',
    backgroundColor: 'white',
    paddingBottom: '20px'
  };

  // Styling for the text labels.
  const labelStyle = {
    margin: '0 10px',
    fontWeight: 'bold',
  };
  const pillContainerStyle = {
    display: 'flex',
    width: '150px', // Adjust width as needed.
    borderRadius: '999px',
    border: '1px solid #ccc',
    overflow: 'hidden',
    cursor: 'pointer',
  };

  // Common styling for each segment.
  const segmentStyle = {
    flex: 1,
    padding: '8px 0',
    textAlign: 'center',
    transition: 'background-color 0.3s, color 0.3s',
    userSelect: 'none',
    fontWeight: 'bold',
  };

  // Left half ("On Market") is active when showOffMarket is false.
  const onMarketStyle = {
    ...segmentStyle,
    backgroundColor: showOffMarket ? 'transparent' : '#007aff',
    color: showOffMarket ? '#2196F3' : '#fff',
  };

  // Right half ("Off Market") is active when showOffMarket is true.
  const offMarketStyle = {
    ...segmentStyle,
    backgroundColor: showOffMarket ? '#2196F3' : 'transparent',
    color: showOffMarket ? '#fff' : '#2196F3',
  };
  // Toggle track styles; background changes based on state.
  const toggleTrackStyle = {
    width: '60px',
    height: '34px',
    borderRadius: '34px',
    backgroundColor: showOffMarket ? '#2196F3' : '#ccc',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.4s',
  };

  // Toggle thumb (circle) styles; position changes based on state.
  const toggleThumbStyle = {
    position: 'absolute',
    top: '4px',
    left: showOffMarket ? '30px' : '4px',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.4s',
  };
  return (
    <div style={pageStyles.pageContainer}>
      {/* Default ZillowSearchBar stays unchanged */}
      <div style={pageStyles.searchBarWrapper}>
        <ZillowSearchBar onSearch={handleSearch} />
      </div>
      <MarketToggle
        showOffMarket={showOffMarket}
        setShowOffMarket={setShowOffMarket}
        offMarketProperties={offMarketProperties}
        onMarketListings={onMarketListings}
        setListings={setListings}
      />
      <div style={pageStyles.contentContainer}>
        {/* Left Panel: Map */}
        <div style={zillowStyles.mapContainer}>
          <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }}>
            <ChangeMapView center={mapCenter} />
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
              attribution='&copy; OpenStreetMap contributors' 
            />
            {!showOffMarket &&
              listings.map((listing) => (
                <Marker
                  key={listing.id}
                  position={[listing.latitude, listing.longitude]}
                  eventHandlers={{ click: () => handleListingClick(listing) }}
                >
                  <Popup>
                    <strong>{listing.formattedAddress}</strong>
                    <br /> ${listing.price.toLocaleString()}
                  </Popup>
                </Marker>
              ))
            }
          </MapContainer>
        </div>

        {/* Right Panel: Listings */}
        <div style={zillowStyles.listingsContainer}>
          {/* When off-market is selected, render the OffMarketSearchBar above the listings */}
          {showOffMarket && (
            <div style={pageStyles.offMarketSearchBarWrapper}>
              <OffMarketSearchBar 
                offMarketProperties={offMarketProperties}
                onSearch={handleOffMarketResults}
              />
            </div>
          )}
          <ul style={zillowStyles.listingsUl}>
            {/* Only render the first visibleCount listings */}
            {listings.slice(0, visibleCount).map((listing) => (
              <ListingItem
                key={listing.id}
                listing={listing}
                onClick={() => handleListingClick(listing)}
                useStreetView={isSearchTriggered}
                mortgage1={Math.round(listing.price * 0.0065)}
              />
            ))}
          </ul>
          {/* Sentinel element to trigger loading more listings when it enters the viewport */}
          <div ref={loadMoreRef} style={{ height: "1px" }}></div>
        </div>
      </div>

      {isModalOpen && <OfferModal listing={selectedListing} onClose={closeModal} />}
      {isOffMarketModalOpen && (
        <OffMarketModal
          listing={selectedListing}
          details={listingOffMarketDetails || {}}  // fallback to empty object
          onClose={closeOffMarketModal}
        />
      )}
    </div>
  );
};
const pageStyles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // Change from '100vh' to '100%'
    width: '100%',
    backgroundColor: 'white',
  },
  searchBarWrapper: {
    width: "100%",
  },
  offMarketSearchBarWrapper: {
    width: "100%",
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'stretch', // This makes children fill the parent's height
    height: "calc(100vh - 80px)",
  },
};

const zillowStyles = {
  mapContainer: {
    flex: 1,
    height: "100%", // Use 100% so it fills the parent's height
    position: 'relative',
    zIndex: 1,
  },
  listingsContainer: {
    flex: 1,
    height: "100%", // Ensures the listings section is the same height
    overflowY: 'auto',
    background: '#fff',
    padding: "20px",
    paddingTop: '0px'
  },
  listingsUl: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    listStyle: 'none',
    padding: 0,
  },
};

const sidebarStyles = {
  backgroundColor: '#f0f0f0', // your desired sidebar color
  width: '250px', // or however wide you want the sidebar
  height: '100%', // fill parent's height
};

// export the ZillowClonePage component
export default ZillowClonePage;
